import { useEffect, useMemo, useState, type FormEvent } from "react";
import { StockImport } from "../../components/StockImport";
import { itemCode, printQrLabels } from "../../components/ScanTools";
import { TruckMoves } from "../../components/JobLoads";
import { useEntitlements } from "../../hooks/useEntitlements";
import { addDoc, collection, doc, limit, onSnapshot, orderBy, query, updateDoc, where, writeBatch } from "firebase/firestore";
import { paths, type InventoryMovement, type PricebookItem, type Vendor } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { ReceiveByInvoice } from "../../components/ReceiveByInvoice";
import { InventoryList } from "../../components/InventoryList";

// Inventory: scan or search, adjust with an audit trail, watch reorder
// levels. Quantities only move through server paths (adjust callable,
// receiving ordered parts, sending invoices) so the movements log is the
// truth. A keyboard-wedge scanner just types the SKU and presses Enter.

type ItemRow = PricebookItem & { id: string };
type VendorRow = Vendor & { id: string };

export function InventoryPage() {
  const { tenantId } = useAuth();
  const { hasFeature } = useEntitlements();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantPricebook(tenantId)), orderBy("name")), (snap) =>
        setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PricebookItem) })).filter((i) => !i.archivedAt)),
      ),
      onSnapshot(collection(db, paths.tenantVendors(tenantId)), (snap) =>
        setVendors(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Vendor) })).filter((v) => !v.archivedAt)),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const tracked = useMemo(() => items.filter((i) => i.trackInventory === true), [items]);
  const lowStock = useMemo(
    () => tracked.filter((i) => i.reorderLevel != null && (i.onHandQty ?? 0) <= i.reorderLevel),
    [tracked],
  );
  // Start / stop counting items (quantities still move only through the server).
  async function setTracking(ids: string[], track: boolean) {
    if (!tenantId) return;
    for (let i = 0; i < ids.length; i += 400) {
      const batch = writeBatch(db);
      for (const id of ids.slice(i, i + 400)) batch.update(doc(db, paths.tenantPricebookItem(tenantId, id)), { trackInventory: track, updatedAt: Date.now() });
      await batch.commit();
    }
  }

  const selected = items.find((i) => i.id === selectedId) ?? null;

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <h1 className="text-lg font-bold">
        Inventory <span className="text-sm font-normal text-steel">{tracked.length} tracked</span>
      </h1>
      {flash ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{flash}</p> : null}

      {hasFeature("invoiceToInventory") ? <ReceiveByInvoice tenantId={tenantId!} /> : null}

      {lowStock.length > 0 ? (
        <Card className="border-danger/40">
          <CardTitle className="text-danger">Below reorder ({lowStock.length})</CardTitle>
          <div className="flex flex-wrap gap-1.5">
            {lowStock.map((i) => (
              <button
                key={i.id}
                className="rounded-full bg-danger/10 px-2.5 py-1 text-xs text-danger hover:bg-danger/20"
                onClick={() => setSelectedId(i.id)}
              >
                {i.name} · {i.onHandQty ?? 0} left{i.onTruckQty ? ` · ${i.onTruckQty} on trucks` : ""}
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {selected ? (
        <ItemPanel tenantId={tenantId!} item={selected} onClose={() => setSelectedId(null)} onFlash={setFlash} />
      ) : null}

      <InventoryList
        items={items}
        selectedId={selectedId}
        onSelect={(id) => {
          setSelectedId(id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onScanMiss={() => setFlash("No item has that SKU. Showing name matches instead.")}
        setTracking={setTracking}
        onPrintLabels={(rows) =>
          // The yard's binder: labels grouped by category (door size / type), then name.
          void printQrLabels(
            [...rows]
              .sort((a, b) => (a.category || "").localeCompare(b.category || "") || a.name.localeCompare(b.name, undefined, { numeric: true }))
              .map((r) => ({ code: itemCode(r), title: r.name, subtitle: [r.category, r.sku, r.bin ? `Bin ${r.bin}` : ""].filter(Boolean).join(" · ") })),
          ).catch((err) => setFlash((err as Error).message))
        }
        say={setFlash}
      />

      <StockImport tenantId={tenantId!} items={items} onDone={setFlash} />

      <VendorsCard tenantId={tenantId!} vendors={vendors} />
    </div>
  );
}

function ItemPanel({
  tenantId,
  item,
  onClose,
  onFlash,
}: {
  tenantId: string;
  item: ItemRow;
  onClose: () => void;
  onFlash: (m: string) => void;
}) {
  const [delta, setDelta] = useState("");
  const [exact, setExact] = useState("");
  const [note, setNote] = useState("");
  const [reorder, setReorder] = useState(item.reorderLevel != null ? String(item.reorderLevel) : "");
  const [bin, setBin] = useState(item.bin ?? "");
  const [sku, setSku] = useState(item.sku ?? "");
  const [busy, setBusy] = useState(false);
  const [movements, setMovements] = useState<Array<InventoryMovement & { id: string }>>([]);

  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, paths.tenantInventoryMovements(tenantId)),
        where("itemId", "==", item.id),
        orderBy("at", "desc"),
        limit(8),
      ),
      (snap) => setMovements(snap.docs.map((d) => ({ id: d.id, ...(d.data() as InventoryMovement) }))),
    );
  }, [tenantId, item.id]);

  async function apply(d: number) {
    if (!Number.isFinite(d) || d === 0) return;
    setBusy(true);
    try {
      await call("adjustInventory", { tenantId, itemId: item.id, delta: Math.round(d), note });
      setDelta("");
      setExact("");
      setNote("");
      onFlash(`${item.name}: ${d > 0 ? "+" : ""}${Math.round(d)}`);
    } catch (err) {
      onFlash((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveMeta() {
    await updateDoc(doc(db, paths.tenantPricebookItem(tenantId, item.id)), {
      sku: sku.trim(),
      bin: bin.trim(),
      reorderLevel: reorder.trim() === "" ? null : Math.max(0, parseInt(reorder, 10) || 0),
      trackInventory: true,
      updatedAt: Date.now(),
    });
    onFlash("Saved.");
  }

  return (
    <Card className="border-brandorange/50">
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">
          {item.name}
          <span className="ml-2 text-sm font-normal text-steel">
            in the yard: <strong className="text-ink">{item.onHandQty ?? 0}</strong>
            {item.onTruckQty ? (
              <>
                {" "}
                · on trucks: <strong className="text-ink">{item.onTruckQty}</strong>
              </>
            ) : null}
          </span>
        </CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" onClick={onClose}>
          Close
        </Button>
      </div>

      <TruckMoves tenantId={tenantId} item={item} onFlash={onFlash} />

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div>
          <Label>Adjust by (+/−)</Label>
          <Input type="number" className="w-24" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+5" />
        </div>
        <Button className="px-3 py-1.5 text-xs" busy={busy} disabled={!delta.trim()} onClick={() => void apply(parseInt(delta, 10) || 0)}>
          Apply
        </Button>
        <span className="pb-2 text-xs text-steel">or</span>
        <div>
          <Label>Set exact count</Label>
          <Input type="number" min="0" className="w-24" value={exact} onChange={(e) => setExact(e.target.value)} placeholder="12" />
        </div>
        <Button
          variant="secondary"
          className="px-3 py-1.5 text-xs"
          busy={busy}
          disabled={exact.trim() === ""}
          onClick={() => void apply((parseInt(exact, 10) || 0) - (item.onHandQty ?? 0))}
        >
          Set
        </Button>
        <div className="min-w-40 flex-1">
          <Label>Why? (goes in the log)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="cycle count / damaged / found" />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-steel/15 pt-3">
        <div>
          <Label>SKU</Label>
          <Input className="w-40" value={sku} onChange={(e) => setSku(e.target.value)} />
        </div>
        <div>
          <Label>Bin / location</Label>
          <Input className="w-32" value={bin} onChange={(e) => setBin(e.target.value)} />
        </div>
        <div>
          <Label>Reorder at</Label>
          <Input type="number" min="0" className="w-24" value={reorder} onChange={(e) => setReorder(e.target.value)} />
        </div>
        <Button variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => void saveMeta()}>
          Save details
        </Button>
      </div>

      {movements.length > 0 ? (
        <div className="mt-3 border-t border-steel/15 pt-2">
          <p className="mb-1 text-xs font-semibold text-steel">Recent movements</p>
          {movements.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-xs">
              <span className="min-w-0 truncate text-ink">
                <span className={cn("font-semibold", m.delta > 0 ? "text-success" : "text-danger")}>
                  {m.delta > 0 ? "+" : ""}
                  {m.delta}
                </span>{" "}
                · {m.reason}
                {m.note ? ` · ${m.note}` : ""}
                {m.actorName ? ` · ${m.actorName}` : ""}
              </span>
              <span className="ml-2 shrink-0 text-steel">
                → {m.onHandAfter} ·{" "}
                {new Date(m.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function VendorsCard({ tenantId, vendors }: { tenantId: string; vendors: VendorRow[] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", accountNumber: "", phone: "" });
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const vendor: Vendor = {
        name: form.name.trim(),
        accountNumber: form.accountNumber.trim(),
        phone: form.phone.trim(),
        email: "",
        note: "",
        archivedAt: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await addDoc(collection(db, paths.tenantVendors(tenantId)), vendor);
      setForm({ name: "", accountNumber: "", phone: "" });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Vendors ({vendors.length})</CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setOpen((v) => !v)}>
          {open ? "Cancel" : "+ Vendor"}
        </Button>
      </div>
      {open ? (
        <form onSubmit={add} className="mt-2 flex flex-wrap items-end gap-2">
          <div className="min-w-44 flex-1">
            <Label>Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Account #</Label>
            <Input className="w-32" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input className="w-36" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <Button type="submit" busy={busy}>
            Add
          </Button>
        </form>
      ) : null}
      <div className="mt-2">
        {vendors.map((v) => (
          <div key={v.id} className="flex items-center justify-between border-b border-steel/10 py-1.5 text-sm last:border-0">
            <span className="min-w-0 truncate text-ink">{v.name}</span>
            <span className="ml-2 shrink-0 text-xs text-steel">
              {[v.accountNumber ? `acct ${v.accountNumber}` : "", v.phone].filter(Boolean).join(" · ")}
            </span>
          </div>
        ))}
        {vendors.length === 0 && !open ? <p className="text-xs text-steel">No vendors yet.</p> : null}
      </div>
    </Card>
  );
}
